import { buildUrl } from "@payping/utilities-shared/url";
import { Button, Col, Flex, LegacyCard, Row, Space, Tag, Typography, usePaypingContext } from "@payping/web-components";
import { FC, SVGProps } from "react";
import type { ListCardProps } from "@payping/web-components/legacyComponents/List/types";
import { useAppTranslation } from "@/utils/useAppTranslation";
import {
  allDocumentId,
  csharpGithubLinkId,
  invoiceDocumentId,
  nodejsGithubLinkId,
  paymentDocumentId,
  phpGithubLinkId,
  postmanTemplateDownloadId,
  reportDocumentId,
  sdkGithubLinkId,
  webserviceHelpId,
  withdrawDocumentId,
} from "@/assets/googleTrackIds";
import { ArrowLeftOutlined, Faktor, Gozaresh, OpenBlank, Pardakht, Tasvie } from "@payping/icons";
import { URLS } from "@payping/utilities-shared/consts";

const Document: FC = () => {
  const { t } = useAppTranslation();
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;

  const sampleDataSource: ListCardProps["dataSource"] = [
    {
      key: "codeSampleSdkSrc",
      image: {
        type: "avatar",
        content: buildUrl(URLS.STORAGE_URL, "img/document/sample_sdk.png").toString(),
        size: 54,
      },
      actions: [
        <a
          key={"codeSampleSdkSrc"}
          href={buildUrl(`https://${URLS.BASE_URL}`, "sdk").toString()}
          data-google-track={sdkGithubLinkId}
          target={"_blank"}
          rel="noreferrer"
        >
          <Space>
            {t("github")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
      descriptions: <Typography.Text>{t("ipg.document.codeSample.sdk")}</Typography.Text>,
    },
    {
      key: "codeSampleCSharpSrc",
      image: {
        type: "avatar",
        content: buildUrl(URLS.STORAGE_URL, "img/document/sample_csharp.png").toString(),
        size: 54,
      },
      actions: [
        <a
          key={"codeSampleCSharpSrc"}
          href={buildUrl(URLS.PPNG_URL, "/t/example-dotnet-payment").toString()}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={csharpGithubLinkId}
        >
          <Space>
            {t("github")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
      descriptions: <Typography.Text>{t("ipg.document.codeSample.csharp")}</Typography.Text>,
    },
    {
      key: "codeSamplePhpSrc",
      image: {
        type: "avatar",
        content: buildUrl(URLS.STORAGE_URL, "img/document/sample_php.png").toString(),
        size: 54,
      },
      actions: [
        <a
          key={"codeSamplePhpSrc"}
          href={buildUrl(URLS.PPNG_URL, "/t/example-php-payment").toString()}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={phpGithubLinkId}
        >
          <Space>
            {t("github")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
      descriptions: <Typography.Text>{t("ipg.document.codeSample.php")}</Typography.Text>,
    },
    {
      key: "codeSampleNodejsSrc",
      image: {
        type: "avatar",
        content: buildUrl(URLS.STORAGE_URL, "img/document/sample_nodejs.png").toString(),
        size: 54,
      },
      actions: [
        <a
          key={"codeSampleNodejsSrc"}
          href={buildUrl(URLS.PPNG_URL, "/t/example-nodejs-payment").toString()}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={nodejsGithubLinkId}
        >
          <Space>
            {t("github")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
      descriptions: <Typography.Text>{t("ipg.document.codeSample.nodejs")}</Typography.Text>,
    },
  ];

  // IconStandard For WebService Section
  type WebServiceIcon = (props: SVGProps<SVGSVGElement>) => JSX.Element;
  const webServicesIcon = (Icon: WebServiceIcon) => (
    <Flex justify={"center"} align={"center"} className={"tw:bg-color-bg-layout tw:p-4!"}>
      <Icon width={54} height={54} className={"tw:border-none tw:bg-color-bg-layout"} color={"black"} />
    </Flex>
  );

  const webServicesTitle = (text: string) => (
    <Typography.Title level={5} className={"tw:font-medium"}>
      {text}
    </Typography.Title>
  );

  const webServicesDataSource: ListCardProps["dataSource"] = [
    {
      key: "paymentDocument",
      image: {
        type: "icon",
        content: webServicesIcon(Pardakht),
      },
      title: {
        primary: webServicesTitle(t("document.payment")),
      },
      content: [
        <Space key={"paymentDocument"} size={"small"}>
          <Tag>{t("settlement.requiredWebServices.help")}</Tag>
          <Tag>Payment</Tag>
        </Space>,
      ],
      actions: [
        <a
          key={"paymentDocument"}
          href={`${URLS.DOCS_URL}/#tag/Payment`}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={paymentDocumentId}
        >
          <Space>
            {t("goto")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
    },
    {
      key: "reportDocument",
      image: { type: "icon", content: webServicesIcon(Gozaresh) },
      title: {
        primary: webServicesTitle(t("document.report")),
      },
      content: [
        <Space key={"reportDocument"} size={"small"}>
          <Tag>{t("settlement.requiredWebServices.help")}</Tag>
          <Tag>Report</Tag>
        </Space>,
      ],
      actions: [
        <a
          key={"reportDocument"}
          href={`${URLS.DOCS_URL}/#tag/Report`}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={reportDocumentId}
        >
          <Space>
            {t("goto")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
    },
    {
      key: "withdrawDocument",
      image: {
        type: "icon",
        content: webServicesIcon(Tasvie),
      },
      title: {
        primary: webServicesTitle(t("document.withdraw")),
      },
      content: [
        <Space key={"withdrawDocument"} size={"small"}>
          <Tag>{t("settlement.requiredWebServices.help")}</Tag>
          <Tag>Withdraw</Tag>
        </Space>,
      ],
      actions: [
        <a
          key={"withdrawDocument"}
          href={`${URLS.DOCS_URL}/#tag/Withdraw`}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={withdrawDocumentId}
        >
          <Space>
            {t("goto")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
    },
    {
      key: "invoiceDocument",
      image: {
        type: "icon",
        content: webServicesIcon(Faktor),
      },
      title: {
        primary: webServicesTitle(t("document.invoice")),
      },
      content: [
        <Space key={"invoiceDocument"} size={"small"}>
          <Tag>{t("settlement.requiredWebServices.help")}</Tag>
          <Tag>Invoice</Tag>
        </Space>,
      ],
      actions: [
        <a
          key={"invoiceDocument"}
          href={`${URLS.DOCS_URL}/#tag/Invoice`}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={invoiceDocumentId}
        >
          <Space>
            {t("goto")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
    },
  ];

  return (
    <>
      {/* <WebServices> */}
      <Row gutter={[0, 16]}>
        {/* WebService Title Col */}
        <Col span={24}>
          <Typography.Title level={4}>{t("webServices")}</Typography.Title>
        </Col>
        {/* WebService Cards Col */}
        <Col xs={24} lg={21}>
          <Flex
            className={!lg ? "tw:-ml-4 tw:overflow-x-scroll" : "tw:overflow-x-scroll"}
            style={{
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <LegacyCard data={webServicesDataSource[0]} />
            <LegacyCard data={webServicesDataSource[1]} />
            <LegacyCard data={webServicesDataSource[2]} />
            <LegacyCard data={webServicesDataSource[3]} />
          </Flex>
        </Col>
        {/* WebService More Col */}
        <Col xs={24} lg={3}>
          <div className={"tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center"}>
            <a href={URLS.DOCS_URL} target={"_blank"} rel="noreferrer" data-google-track={allDocumentId}>
              <Space>
                {t("document.title")}
                <ArrowLeftOutlined width={10} />
              </Space>
            </a>
          </div>
        </Col>
        {/* WebService Description Col */}
        <Col span={24}>
          <Space orientation={"vertical"} size={"large"} className={"tw:w-full"}>
            <Typography.Text className={"line-height-4"}>{t("ipg.document.codeSample.postMan.desc")}</Typography.Text>
            <Space>
              <Button>
                <a
                  href={"https://statics.payping.ir/postman.json"}
                  className={"color--black"}
                  target={"_blank"}
                  rel="noreferrer"
                  data-google-track={postmanTemplateDownloadId}
                >
                  {t("ipg.document.codeSample.postMan.title")}
                </a>
              </Button>
              <a
                href={`${URLS.HELP_URL}/fa/category/api--qspohi/`}
                target={"_blank"}
                rel="noreferrer"
                data-google-track={webserviceHelpId}
              >
                {t("settlement.requiredWebServices.title")}
              </a>
            </Space>
          </Space>
        </Col>
      </Row>
      {/* </WebService> */}

      {/*  <Needed Resource> */}
      <Space orientation={"vertical"} className={"tw:my-12! tw:w-full"}>
        <Row>
          <Col span={24}>
            <Typography.Title level={4}>{t("ipg.document.codeSample.title")}</Typography.Title>
          </Col>
          <Col span={24}>
            <Flex
              className={!lg ? "tw:-ml-4 tw:overflow-x-scroll" : "tw:overflow-x-scroll"}
              style={{
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
            >
              <LegacyCard data={sampleDataSource[0]} />
              <LegacyCard data={sampleDataSource[1]} />
              <LegacyCard data={sampleDataSource[2]} />
              <LegacyCard data={sampleDataSource[3]} />
            </Flex>
          </Col>
        </Row>
      </Space>
      {/*  </Needed Resource> */}
    </>
  );
};

export default Document;
